/**
 * 伺服器時間校準（後端要求：X-Timestamp 取後端回應裡的 serverTime，不要前端自己生成）。
 *
 * 為什麼需要：後端以「伺服器當前時間」判斷 X-Timestamp 是否落在時效窗口內
 * （[伺服器當前秒 - 300, 伺服器當前秒 + 300]）。瀏覽器使用者改過系統時間、或裝置時鐘漂移，
 * 就算簽名算對了也會被拒，所以時間戳要跟後端對齊。
 *
 * 後端的回應信封（成功與失敗都一樣）會帶上秒級的 serverTime，例如
 * { code: 0, message: '', data: { ... }, serverTime: 1654840214 }；
 * 這裡把它換算成「伺服器時間 - 本機時間」的偏移量，之後簽名用的時間戳都取校準後的值。
 * 還沒拿過 serverTime 時偏移量為 0（等於本機時間，用於第一次請求）。
 *
 * 註：只用同一個回應的到達時間估算，誤差是單程網路延遲（毫秒級），
 * 相對於 300 秒的時效窗口可以忽略；BFF 端（bff/src/server-time.js）用往返中點估算。
 */

/** 可能承載伺服器時間的欄位名（信封頂層或 data 內） */
const SERVER_TIME_FIELDS = ['serverTime', 'server_time']

/** 秒級時間戳的上界（毫秒級時間戳是 13 位，必須排除，否則後端會判為非法時間戳） */
const MAX_SECONDS = 1e11

let offsetMs = 0
let syncedAtMs = null
let lastServerTimeSeconds = null

/**
 * 把回應內容轉成物件：字串會先當 JSON 解析（某些回應可能還沒被解析）
 * @param {*} raw 回應內容
 * @returns {Object|null} 轉不出物件時返回 null
 */
function toPayload(raw) {
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) {
      return null
    }
    try {
      return JSON.parse(text)
    } catch (e) {
      return null
    }
  }

  return raw && typeof raw === 'object' ? raw : null
}

/**
 * 從回應內容取出伺服器時間（秒）
 *
 * serverTime 與 code / data 同級，位於回應體頂層，例如
 * { code: 0, message: '', data: { ... }, serverTime: 1654840214 }。
 * 因此直接拿 axios 回應物件（res）時是 res.data.serverTime，拿到的若已經是回應體則是 res.serverTime；
 * 兩種取法都會認，另外也相容 data.serverTime 與 server_time 的寫法。
 * @param {*} payload 回應內容（物件或 JSON 字串）
 * @returns {Number|null} 取不到時返回 null
 */
export function readServerTimeSeconds(payload) {
  payload = toPayload(payload)
  if (!payload) {
    return null
  }

  const sources = [payload, payload.data]
  for (let i = 0; i < sources.length; i++) {
    const source = sources[i]
    if (!source || typeof source !== 'object') {
      continue
    }
    for (let j = 0; j < SERVER_TIME_FIELDS.length; j++) {
      const value = Number(source[SERVER_TIME_FIELDS[j]])
      if (isFinite(value) && value > 0 && value < MAX_SECONDS) {
        return Math.floor(value)
      }
    }
  }

  return null
}

/**
 * 用一次伺服器時間觀測值校準本機時鐘
 * @param {*} payload 回應內容（物件或 JSON 字串）
 * @returns {Boolean} 有校準到時返回 true
 */
export function calibrateFromPayload(payload) {
  const seconds = readServerTimeSeconds(payload)
  if (seconds === null) {
    return false
  }

  const receivedAtMs = Date.now()
  offsetMs = seconds * 1000 - receivedAtMs
  syncedAtMs = receivedAtMs
  lastServerTimeSeconds = seconds
  return true
}

/**
 * axios 回應攔截器用：從回應校準時間。
 * 兩種取法都認——res.serverTime（回應物件本身就帶）與 res.data.serverTime（回應體頂層）；
 * 回應體是 JSON 字串時也會嘗試解析。
 * @param {Object} response axios 回應
 * @returns {Boolean} 有校準到時返回 true
 */
export function calibrateFromResponse(response) {
  if (!response) {
    return false
  }
  if (calibrateFromPayload(response)) {
    return true
  }

  return calibrateFromPayload(response.data)
}

/**
 * 校準後的 Unix 秒級時間戳，直接作為 X-Timestamp。
 * 沒校準過時等同本機時間。
 */
export function getServerTimestamp() {
  return Math.floor((Date.now() + offsetMs) / 1000)
}

/** 是否已經拿過伺服器時間 */
export function isServerTimeSynced() {
  return syncedAtMs !== null
}

/** 本機時間與伺服器時間的偏移量（毫秒，正數表示本機慢） */
export function getServerTimeOffsetMs() {
  return offsetMs
}

/** 最近一次觀測到的伺服器時間（秒）；沒校準過返回 null */
export function getLastServerTime() {
  return lastServerTimeSeconds
}

/** 回到未校準狀態（測試用） */
export function resetServerTime() {
  offsetMs = 0
  syncedAtMs = null
  lastServerTimeSeconds = null
}
